import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './UiComponents/button';

interface SelectComponentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedItems: string[]) => void;
  allItems: string[];
  selectedItems: string[];
  setSelectedItems: (items: string[] | ((prev: string[]) => string[])) => void;
}

const SelectComponentModal: React.FC<SelectComponentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  allItems,
  selectedItems,
  setSelectedItems,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleItem = (item: string) => {
    setSelectedItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const handleSave = () => {
    onSave(selectedItems);
    onClose();
  };

  // Close the modal when clicking outside of it
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onSave(selectedItems);
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-700 border rounded-lg p-6 w-full max-w-2xl text-white dark:text-gray-100 max-h-[80vh] overflow-y-auto "
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-black dark:text-white font-bold">Select Items</h2>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
            abbr="Close modal and cancel selecting items"
          >
            <X size={24} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {allItems.map((item) => (
            <Button
              type="button"
              key={item}
              onClick={() => toggleItem(item)}
              className={`px-4 py-2 rounded-full text-sm ${selectedItems.includes(item)
                  ? 'bg-green-800 text-gray-100 hover:bg-green-900'
                  : 'bg-green-600 text-white'
                }`}
              abbr={selectedItems.includes(item) ? `Click to deselect this item: ${item}` : `Click to select this item: ${item}`}
            >
              {item}
            </Button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button
            variant="default"
            type="button"
            onClick={handleSave}
            abbr='Click to save your selected items'
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectComponentModal;
